import { Injectable } from '@angular/core';
import { routes } from '../routes/routes';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { apiResultFormat, SideBar, SideBar2, SideBarMenu } from '../models/models';
import { CarSearchRequest, City, FlightSearchRequest, HotelSearchRequest, Location } from '../models/search-bar';
import { environment } from '../../../enviroments/environments';
import { AdminSuggestedInfo } from '../../feature-module/admin/admin-suggested-info/admin-suggested-info.model';
import { Country } from './country.model';
import { Language } from './language.model';
import { Currency } from './currency.model';
import { RevenueTransaction } from '../../feature-module/admin/admin-revenue/admin-revenue-listing/admin-revenue-listing.component';

export interface CarBookingResponse {
  content: any[];
  totalPages: number;
  totalElements: number;
  pageable: {
    pageNumber: number;
    pageSize: number;
    offset: number;
    paged: boolean;
    unpaged: boolean;

  };

  number: number;
  size: number;
  numberOfElements: number;
  first: boolean;
  last: boolean;
  empty: boolean;


}
export interface FlightBookingResponse {
  content: any[];
  totalPages: number;
  totalElements: number;
  pageable: {
    pageNumber: number;
    pageSize: number;
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  number: number;
  size: number;
  numberOfElements: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}



@Injectable({
  providedIn: 'root',
})
export class DataService {
  // --- Hotel details / room details state (last request & response) ---
  private _lastHotelDetailsRequest = new BehaviorSubject<any | null>(null);
  public readonly lastHotelDetailsRequest$ = this._lastHotelDetailsRequest.asObservable();

  private _lastHotelDetailsResponse = new BehaviorSubject<any | null>(null);
  public readonly lastHotelDetailsResponse$ = this._lastHotelDetailsResponse.asObservable();

  private _lastHotelRoomDetailsRequest = new BehaviorSubject<{ sessionId: string; productId: string; tokenId: string; rateBasisId: string } | null>(null);
  public readonly lastHotelRoomDetailsRequest$ = this._lastHotelRoomDetailsRequest.asObservable();

  private _lastHotelRoomDetailsResponse = new BehaviorSubject<any | null>(null);
  public readonly lastHotelRoomDetailsResponse$ = this._lastHotelRoomDetailsResponse.asObservable();

  public getRevenueTransaction(): Observable<RevenueTransaction[]> {
    return this.http.get<RevenueTransaction[]>(`${environment.apiUrl}/revenue/transactions`);
  }
  getCommissionByFare(fare: number) {
    return this.http.get<any>(`${environment.apiUrl}/commissions/fare?fare=${fare}`);
  }
  getAllCommissions(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/commissions`);
  }


  getHotelLanguages(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/common/hotel-languages`);
  }

  getCarLanguages(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/common/car-languages`);
  }
  getCountries(): Observable<Country[]> {
    return this.http.get<Country[]>(`${environment.apiUrl}/common/countries`);
  }

  getLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(`${environment.apiUrl}/common/languages`);
  }

  getCurrencies(): Observable<Currency[]> {
    return this.http.get<Currency[]>(`${environment.apiUrl}/common/currencies`);
  }
  // --- Supported Info CRUD ---
  getSupportedInfo(): Observable<AdminSuggestedInfo[]> {
    return this.http.get<AdminSuggestedInfo[]>(`${environment.apiUrl}/common/supported-info`);
  }

  getSupportedById(id: number): Observable<AdminSuggestedInfo> {
    return this.http.get<AdminSuggestedInfo>(`${environment.apiUrl}/common/supported-info/${id}`);
  }

  createSupportedInfo(data: Partial<AdminSuggestedInfo>): Observable<AdminSuggestedInfo> {
    return this.http.post<AdminSuggestedInfo>(`${environment.apiUrl}/common/supported-info`, data);
  }

  updateSupportedInfo(id: number, data: Partial<AdminSuggestedInfo>): Observable<AdminSuggestedInfo> {
    return this.http.put<AdminSuggestedInfo>(`${environment.apiUrl}/common/supported-info/${id}`, data);
  }

  deleteSupportedInfo(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/common/supported-info/${id}`);
  }

  private readonly collapseSubject = new BehaviorSubject<boolean>(false);
  collapse$ = this.collapseSubject.asObservable();

  toggleCollapse() {
    this.collapseSubject.next(!this.collapseSubject.value);
  }
  constructor(private readonly http: HttpClient) { }

  getStaticContent() {
    return this.http.get<any>(`${environment.apiUrl}/static-content/all`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getStaticContentByType(type: string) {
    return this.http.get<any>(`${environment.apiUrl}/static-content/${type}`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }


  getBaseContentItems() {
    return this.http.get<any>(`${environment.apiUrl}/special/all`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  saveBasicContent(data: any) {
    const id = data?.id;
    return this.http.post<any>(`${environment.apiUrl}/special${id ? '/' + id : ''}`, data).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  saveStaticContent(data: any, isEdit: boolean = false) {
    if (!isEdit) {
      // If no ID is present, it's a new static content creation
      return this.http.post<any>(`${environment.apiUrl}/static-content`, data).pipe(
        map((res: any) => {
          return res;
        })
      );
    }
    return this.http.put<any>(`${environment.apiUrl}/static-content/${data?.type}`, data).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getBasicContentById(id: any) {
    return this.http.get<any>(`${environment.apiUrl}/special/${id}`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getAllFaq() {
    return this.http.get<any>(`${environment.apiUrl}/faqs`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getAdminYearlyStats(year: any): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/admin/getMonthlyTransactionCountChart?year=` + year).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getDashboardStats(): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/admin/getDashboardStats`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }
  getRecentTransactions(): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/admin/recent-transactions?page=0&size=5`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  public getAirportList(searchString: string, lat?: number, lon?: number): Observable<Location[]> {
    const params: any = {};
    if (lat !== undefined) {
      params.lat = lat;
    } else {
      params.lat = 0;
    }

    if (lon !== undefined) {
      params.lon = lon;
    } else {
      params.lon = 0;
    }
    return this.http
      .get<any[]>(`${environment.apiUrl}/airport/search/${searchString}`, { params })
      .pipe(map((res: any[]) => res.map(r => ({
        code: r.AirportCode,
        name: r.AirportName,
        city: r.City,
        country: r.Country,
        latitude: r.Latitude,
        longitude: r.Longitude,
      }) as Location)
      )
      );
  }

  public getCarLocationList(searchString: string, lat?: number, lon?: number): Observable<Location[]> {
    const params: any = {};
    if (lat !== undefined) {
      params.lat = lat;
    } else {
      params.lat = 0;
    }

    if (lon !== undefined) {
      params.lon = lon;
    } else {
      params.lon = 0;
    }
    return this.http
      .get<any[]>(`${environment.apiUrl}/car/search/${searchString}`, { params })
      .pipe(map((res: any[]) => res.map(r => ({
        id: r.id,
        code: r.locationCode,
        name: r.locationName,
        city: r.city,
        country: r.countryCode,
        latitude: r.latitude,
        longitude: r.longitude,
      }) as Location)
      )
      );
  }

  public getCarLocationById(id: number | string,lat?: number, lon?: number): Observable<Location | null> {
     const params: any = {};
    if (lat !== undefined) {
      params.lat = lat;
    } else {
      params.lat = 0;
    }

    if (lon !== undefined) {
      params.lon = lon;
    } else {
      params.lon = 0;
    }
    return this.http.get<any>(`${environment.apiUrl}/car/search/${id}`,{params}).pipe(
      map((res: any) => {
        const pick = (r: any): Location => ({
          id: r?.id,
          code: r?.locationCode ?? r?.code,
          name: r?.locationName ?? r?.name,
          city: r?.city ?? null,
          country: r?.countryCode ?? r?.country,
          latitude: r?.latitude,
          longitude: r?.longitude,
        } as Location);
        if (!res) return null;
        if (Array.isArray(res)) {
          const q = String(id);
          const match = res.find((r: any) => String(r?.id) === q || String(r?.locationCode ?? r?.code) === q) || res[0];
          return match ? pick(match) : null;
        }
        return pick(res);
      })
    );
  }

  public getCityList(searchString: string, lat?: number, lon?: number): Observable<City[]> {
    console.log('DataService called with:', searchString, lat, lon);
    const params: any = {};
    if (lat !== undefined) {
      params.lat = lat;
    } else {
      params.lat = 0;
    }

    if (lon !== undefined) {
      params.lon = lon;
    } else {
      params.lon = 0;
    }
    return this.http.get<any[]>(`${environment.apiUrl}/city/search/${encodeURIComponent(searchString)}`,
      { params }
    ).pipe(
      map(res =>
        res.map(r => ({
          id: r.id,
          cityName: r.cityName,
          countryName: r.countryName,
          latitude: r.latitude,
          longitude: r.longitude
        }) as City)
      )
    );
  }

  public getCityById(id: number): Observable<City> {
    return this.http.get<any>(`${environment.apiUrl}/city/search/id/${id}`).pipe(
      map((r: any) => {
        return {
          id: r?.id,
          cityName: r?.cityName,
          countryName: r?.countryName,
          latitude: r?.Latitude,
          longitude: r?.Longitude,
        } as City
      })
    )
  }

  // Nearest lookups
  getNearestCity(lat: number, lon: number): Observable<City | null> {
    return this.http.get<any>(`${environment.apiUrl}/city/nearest`, { params: { lat, lon } as any }).pipe(
      map((r: any) => {
        if (!r) return null;
        return {
          id: r?.id,
          cityName: r?.cityName,
          countryName: r?.countryName,
          latitude: r?.Latitude ?? r?.latitude,
          longitude: r?.Longitude ?? r?.longitude,
        } as City;
      })
    );
  }

  getNearestAirport(lat: number, lon: number) {
    return this.http.get<any>(`${environment.apiUrl}/airport/nearest`, { params: { lat, lon } as any }).pipe(
      map((r: any) => r ? ({
        code: r.AirportCode ?? r.code,
        name: r.AirportName ?? r.name,
        city: r.City ?? r.city,
        country: r.Country ?? r.country,
        latitude: r.Latitude ?? r.latitude,
        longitude: r.Longitude ?? r.longitude,
      }) : null)
    );
  }

  getNearestCarLocation(lat: number, lon: number) {
    return this.http.get<any>(`${environment.apiUrl}/car/nearest`, { params: { lat, lon } as any }).pipe(
      map((r: any) => r ? ({
        id: r.id,
        code: r.locationCode ?? r.code,
        name: r.locationName ?? r.name,
        city: r.city ?? null,
        country: r.countryCode ?? r.country,
        latitude: r.latitude,
        longitude: r.longitude,
      }) : null)
    );
  }

  getAllAirLines(): void {
    this.http.get<any[]>(`${environment.apiUrl}/airline`).pipe(
      tap((res: any[]) => {
        localStorage.setItem('airlines', JSON.stringify(res));
      })
    ).subscribe();
  }

  getAirline(airlineCode: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/airline/search/${airlineCode}`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  public getFlightSearchData(request: FlightSearchRequest): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/flight/search`, request).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  fetchFlightDetails(payload: any) {
    return this.http.post<any>(`${environment.apiUrl}/flight/reValidate`, payload).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  public getFlightBookingDetails(bookingId: string): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/flight/tripDetails`, {
      UniqueID: bookingId
    });
  }

  getCountryCodes() {
    return this.http.get<any[]>(`${environment.apiUrl}/common/countries`).pipe(
      map((res: any[]) => res)
    );
  }

  confirmBookFlight(paymentIntentId: any) {
    return this.http.post<any>(`${environment.apiUrl}/flight/confirmBooking`, { paymentIntentId: paymentIntentId }).pipe(
      map((res: any) => {
        return res;
      })
    );
  }
  public getPaymentInfo(paymentIntent: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/payment/status/${paymentIntent}`);
  }
  getOrderDetails(orderId: string) {
    return this.http.get<any>(`${environment.apiUrl}/flight/transaction/${orderId}`).pipe(
      map((res: any) => res)
    );
  }
  getHotelOrderDetails(orderId: string) {
    return this.http.get<any>(`${environment.apiUrl}/hotel/transaction/${orderId}`).pipe(
      map((res: any) => res)
    );
  }
  initiateFlightBooking(travellerData: any) {
    return this.http.post<any>(`${environment.apiUrl}/flight/initiateBooking`, travellerData).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  cancelFlightBooking(payload: { UniqueID: string }) {
    return this.http.post<any>(`${environment.apiUrl}/flight/booking-cancel`, payload).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  /**
   * Get cancellation quote for a flight booking
   * Expects payload: { UniqueID: string, paxDetails: [...] }
   */
  cancellationQuote(payload: any) {
    return this.http.post<any>(`${environment.apiUrl}/flight/cancellation-quote`, payload).pipe(
      map((res: any) => res)
    );
  }

  ptrStatus(payload: { UniqueID: string, ptrUniqueID?: string }) {
    return this.http.post<any>(`${environment.apiUrl}/flight/pnr-status`, payload).pipe(
      map((res: any) => res)
    );
  }

  /**
   * Perform cancellation of a flight booking
   * Expects payload: { UniqueID: string, paxDetails: [...] }
   */
  cancellation(payload: any) {
    return this.http.post<any>(`${environment.apiUrl}/flight/cancellation`, payload).pipe(
      map((res: any) => res)
    );
  }

  getHotelSearchData(value: HotelSearchRequest) {
    return this.http.post<any>(`${environment.apiUrl}/hotel/search`, value).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getCarSearchData(value: CarSearchRequest) {
    return this.http.post<any>(`${environment.apiUrl}/car/search`, value).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getCarDetails(payload: { session_id: string, reference_id: string }) {
    return this.http.post<any>(`${environment.apiUrl}/car/detail`, payload).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getInsurancePlans(payload: { session_id: string, reference_id: string, first_name: string, last_name: string }) {
    return this.http.post<any>(`${environment.apiUrl}/car/carInsurance`, payload).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getCarBookingDetails(confirmationId: string) {
    return this.http.post<any>(`${environment.apiUrl}/car/bookingDetails`, { confirmation_id: confirmationId }).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  submitCarBooking(payload: any) {
    return this.http.post<any>(`${environment.apiUrl}/car/carBooking`, payload).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getHotelMoreData(payload: { sessionId: any; nextToken: any; }) {
    return this.http.get<any>(`${environment.apiUrl}/hotel/more-results`, { params: payload }).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getHotelSearchDataWithFilter(value: any) {
    return this.http.post<any>(`${environment.apiUrl}/hotel/filter`, value).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getHotelRooms(value: any) {
    return this.http.post<any>(`${environment.apiUrl}/hotel/roomRates`, value).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getHotelMoreDataWithFilter(payload: { sessionId: any; nextToken: any; filterKey?: string }) {
    return this.http.get<any>(`${environment.apiUrl}/hotel/more-filterSearch`, { params: payload }).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getHotelDetails(payload: any) {
    // store last hotel details request
    this._lastHotelDetailsRequest.next(payload);
    return this.http.get<any>(`${environment.apiUrl}/hotel/details`, { params: payload }).pipe(
      tap((res: any) => this._lastHotelDetailsResponse.next(res)),
      map((res: any) => {
        return res;
      })
    );
  }

  /**
   * Fetch room details for a hotel (check-roomRates endpoint)
   * @param payload { sessionId, productId, tokenId, rateBasisId }
   */
  getHotelRoomDetails(payload: { sessionId: string; productId: string; tokenId: string; rateBasisId: string }): Observable<any> {
    // store last hotel room-details request
    this._lastHotelRoomDetailsRequest.next(payload);
    return this.http.post<any>(`${environment.apiUrl}/hotel/check-roomRates`, payload).pipe(
      tap((res: any) => this._lastHotelRoomDetailsResponse.next(res)),
      map((res: any) => res)
    );
  }

  initiateHotelBooking(bookingRequest: any) {
    return this.http.post<any>(`${environment.apiUrl}/hotel/initiate-booking`, bookingRequest).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  confirmBookHotel(paymentIntentId: any) {
    return this.http.post<any>(`${environment.apiUrl}/hotel/confirm-booking`, { paymentIntentId: paymentIntentId }).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  cancelBooking(payload: { referenceNum: string, supplierConfirmationNum: string }) {
    return this.http.post<any>(`${environment.apiUrl}/hotel/booking-cancel`, payload).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getBookingDetails(payload: { referenceNum: string, supplierConfirmationNum: string }) {
    return this.http.post<any>(`${environment.apiUrl}/hotel/booking-details`, payload).pipe(
      map((res: any) => {
        return res;
      })
    );
  }
  getLoggedInUserFlightBookings() {
    return this.http.get<any>(`${environment.apiUrl}/flight/loggedInUserBooking`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }



  getAllFlightBookings(
    page: number,
    size: number,
    sortField?: string,
    sortDirection?: string
  ): Observable<FlightBookingResponse> {
    const body: any = { page, size };

    if (sortField && sortDirection) {
      body.sort = `${sortField},${sortDirection}`;
    }

    return this.http.post<FlightBookingResponse>(
      `${environment.apiUrl}/admin/all-flight-transactions`,
      body
    );
  }




  getAllHotelBookings(page: number, size: number): Observable<any> {
    return this.http.post(`${environment.apiUrl}/admin/all-hotel-transactions`, { page, size });
  }



  getHotelCancelledTransactions() {
    return this.http.get<any>(`${environment.apiUrl}/admin/hotel-transactions/cancelled-initiated`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  /**
   * Mark a hotel booking as refunded in backend.
   * Expects payload: { supplierConfirmationNum: string, refundedNote: string }
   */
  markHotelRefunded(supplierConfirmationNum: string, refundedNote: string) {
    const payload = { supplierConfirmationNum, refundedNote };
    return this.http.post<any>(`${environment.apiUrl}/hotel/mark-refunded`, payload).pipe(
      map((res: any) => res)
    );
  }

  /**
   * Mark a flight booking as refunded in backend.
   * Expects payload: { uniqueID: string, refundedNote: string }
   */
  markFlightRefunded(uniqueID: string, refundedNote: string) {
    const payload = { uniqueID, refundedNote };
    return this.http.post<any>(`${environment.apiUrl}/flight/mark-refunded`, payload).pipe(
      map((res: any) => res)
    );
  }

  getFlightCancelledTransactions() {
    return this.http.get<any>(`${environment.apiUrl}/admin/flight-transactions/cancelled-initiated`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getAllCarBookings(page: number, size: number): Observable<CarBookingResponse> {
    return this.http.post<CarBookingResponse>(`${environment.apiUrl}/admin/all-car-transactions`, { page, size });
  }



  getLoggedInUserHotelBookings() {
    return this.http.get<any>(`${environment.apiUrl}/hotel/loggedInUserBooking`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }
  getLoggedInUserCarBookings() {
    return this.http.get<any>(`${environment.apiUrl}/car/getLoggedInUserBooking`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }
  cancelCarBooking(payload: { confirmation_id: string }) {
    return this.http.post<any>(`${environment.apiUrl}/car/booking-cancel`, payload).pipe(
      map((res: any) => {
        return res;
      })
    );
  }
  getFaqById(id: any) {
    return this.http.get<any>(`${environment.apiUrl}/faqs/${id}`).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  saveFaq(faqData: any) {
    return this.http.post<any>(`${environment.apiUrl}/faqs`, faqData).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  public getDataTable(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/data-tables.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getFlightBooking(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/flight-booking.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getEnquiry(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/enquiry.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getEarnings(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/earning.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getwithdraw(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/withdraw.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getHotelBooking(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/hotel-booking.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getCarBooking(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/car-booking.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }

  public getCruiseBooking(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/cruise-booking.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getTourBooking(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/tour-booking.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getWallet(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/wallet.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getPayment(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/payment.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getAgentDashboard(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/agent-dashboard.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getAgentHotel(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/agent-hotel-booking.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getAgentFlight(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/agent-flight-booking.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getAgentCar(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/agent-car-booking.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getAgentCruise(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/agent-cruise-booking.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public getAgentTour(): Observable<apiResultFormat> {
    return this.http.get<apiResultFormat>('assets/json/agent-tour-booking.json').pipe(
      map((res: apiResultFormat) => {
        return res;
      })
    );
  }
  public sideBar2: SideBar2[] = [
    {
      tittle: 'Main',
      base: 'user',
      showAsTab: false,
      separateRoute: false,
      menu: [
        {
          menuValue: 'Dashboard',
          route: routes.userDashboard,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'dashboard',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-grid-55'
        },
        {
          menuValue: 'Bookings',
          hasSubRoute: true,
          showSubRoute: false,
          icon: 'isax-calendar-tick5',
          base: 'customer-flight-booking',
          base2: 'customer-hotel-booking',
          base3: 'customer-car-booking',
          base4: 'customer-cruise-booking',
          base5: 'customer-tour-booking',
          subMenus: [
            {
              menuValue: 'Flights',
              route: routes.customerFlightBooking,
              hasSubRoute: false,
              base: "customer-flight-booking"
            },
            {
              menuValue: 'Hotels',
              route: routes.customerHotelBooking,
              hasSubRoute: false,
              base: "customer-hotel-booking"
            },
            {
              menuValue: 'Cars',
              route: routes.customerCarBooking,
              hasSubRoute: false,
              base: "customer-car-booking"
            },
            {
              menuValue: 'Cruise',
              route: routes.customerCruiseBooking,
              hasSubRoute: false,
              base: "customer-cruise-booking"
            },
            {
              menuValue: 'Tour',
              route: routes.customerTourBooking,
              hasSubRoute: false,
              base: "customer-tour-booking"
            },
          ],
        },
        {
          menuValue: 'My Reviews',
          route: routes.review,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'review',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-magic-star5'
        },
        {
          menuValue: 'Messages',
          route: routes.chat,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'message',
          count: '1',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-message-square5'
        },
        {
          menuValue: 'Wishlist',
          route: routes.wishlist,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'wishlist',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-heart5'
        },
      ]
    },
    {
      tittle: 'Finance',
      base: 'wallet',
      showAsTab: false,
      separateRoute: false,
      menu: [
        {
          menuValue: 'Wallet',
          route: routes.wallet,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'wallet',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-wallet-add-15'
        },
        {
          menuValue: 'Payments',
          route: routes.payment,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'payemnt',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-money-recive5'
        },
      ]
    },
    {
      tittle: 'Account',
      base: 'my-profile',
      showAsTab: false,
      separateRoute: false,
      menu: [
        {
          menuValue: 'My Profile',
          route: routes.myProfile,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'my-profile',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-profile-tick5'
        },
        {
          menuValue: 'Notifications',
          route: routes.notification,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'notification',
          count: '2',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-notification-bing5'
        },
        {
          menuValue: 'Settings',
          route: routes.profileSettings,
          hasSubRoute: false,
          showSubRoute: false,
          base5: 'profile-settings',
          base2: 'notification-settings',
          base3: 'security-settings',
          base4: 'integration-settings',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-notification-bing5'
        },
        {
          menuValue: 'Logout',
          route: routes.index,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'index',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-notification-bing5'
        },
      ]
    }
  ]
  public getSideBarData2: BehaviorSubject<Array<SideBar2>> = new BehaviorSubject<
    Array<SideBar2>
  >(this.sideBar2);
  public agentSideBar: SideBar2[] = [
    {
      tittle: 'Main',
      base: 'dashboard',
      showAsTab: false,
      separateRoute: false,
      menu: [
        {
          menuValue: 'Dashboard',
          route: routes.agentDashboard,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'agent-dashboard',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-grid-55'
        },
        {
          menuValue: 'Listings',
          route: routes.agentListings,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'listings',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-menu-14'
        },
        {
          menuValue: 'Bookings',
          hasSubRoute: true,
          showSubRoute: false,
          icon: 'isax-calendar-tick5',
          base: 'fligt-booking',
          base2: 'hotel-booking',
          base3: 'car-booking',
          base4: 'cruise-booking',
          base5: 'tour-booking',
          subMenus: [
            {
              menuValue: 'Hotels',
              route: routes.agentHotelBooking,
              hasSubRoute: false,
              base: "hotel-booking"
            },
            {
              menuValue: 'Cars',
              route: routes.agentCarBooking,
              hasSubRoute: false,
              base: "car-booking"
            },
            {
              menuValue: 'Cruise',
              route: routes.agentCruiseBooking,
              hasSubRoute: false,
              base: "cruise-booking"
            },
            {
              menuValue: 'Tour',
              route: routes.agentTourBooking,
              hasSubRoute: false,
              base: "tour-booking"
            },
            {
              menuValue: 'Flights',
              route: routes.agentFlightBooking,
              hasSubRoute: false,
              base: "fligt-booking"
            },



          ],
        },
        {
          menuValue: 'Enquiries',
          route: routes.agentEnquirers,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'enquirers',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-magic-star5'
        },
        {
          menuValue: 'Earnings',
          route: routes.agentEarnings,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'earnings',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-wallet-add-15'
        },
        {
          menuValue: 'Reviews',
          route: routes.agentReview,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'reviews',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-magic-star5'
        },
        {
          menuValue: 'Settings',
          route: routes.agentSettings,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'settings',
          base2: 'account-settings',
          base3: 'security-settings',
          base4: 'plans-settings',
          page: '',
          last: '',
          subMenus: [],
          icon: 'isax-setting-25'
        },

      ]
    },

  ]
  public getAgentSideBar: BehaviorSubject<Array<SideBar2>> = new BehaviorSubject<
    Array<SideBar2>
  >(this.agentSideBar);
  public resetData2(): void {
    this.sideBar2.forEach((res: SideBar2) => {
      res.showAsTab = false;
      res.menu.forEach((menus: SideBarMenu) => {
        menus.showSubRoute = false;
      });
    });
  }
  public sideBar: SideBar[] = [
    {
      tittle: 'Home',
      base: 'index',
      base1: 'index-2',
      base2: 'index-3',
      base3: 'index-4',
      base4: 'index-5',
      base5: 'index-6',
      showAsTab: false,
      separateRoute: true,
      menu: [
        {
          menuValue: 'Home - 1',
          img: 'assets/img/menu/home-01.jpg',
          homeName: 'All Bookings',
          route: routes.home,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'index',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Home - 2',
          img: 'assets/img/menu/home-02.jpg',
          route: routes.home2,
          homeName: 'Hotels',
          hasSubRoute: false,
          showSubRoute: false,
          base: 'index-2',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Home - 3',
          img: 'assets/img/menu/home-03.jpg',
          route: routes.home3,
          homeName: 'Cars',
          hasSubRoute: false,
          showSubRoute: false,
          base: 'index-3',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Home - 4',
          img: 'assets/img/menu/home-04.jpg',
          route: routes.home4,
          homeName: 'Flight',
          hasSubRoute: false,
          showSubRoute: false,
          base: 'index-4',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Home - 5',
          img: 'assets/img/menu/home-05.jpg',
          route: routes.home5,
          homeName: 'Cruise',
          hasSubRoute: false,
          showSubRoute: false,
          base: 'index-5',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Home - 6',
          img: 'assets/img/menu/home-06.jpg',
          route: routes.home6,
          homeName: 'Tours',
          hasSubRoute: false,
          showSubRoute: false,
          base: 'index-6',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
      ],
    },
    {
      tittle: 'Flight',
      base: 'flight',
      subTitle: 'Flight Booking',
      showAsTab: false,
      separateRoute: false,
      twoTitle: false,
      img: 'assets/img/menu/flight.jpg',
      menu: [
        {
          menuValue: 'Flight Grid',
          route: routes.flightGrid,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'flight-grid',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Flight List',
          route: routes.flightList,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'flight-list',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Flight Details',
          route: routes.flightDetails,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'flight-details',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Flight Booking',
          route: routes.flightBookingConfirmation,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'flight-booking-confirmation',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Add Flight',
          route: routes.addFlight,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'add-flight',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
      ],
    },
    {
      tittle: 'Hotel',
      base: 'hotel',
      subTitle: 'Hotel Booking',
      showAsTab: false,
      separateRoute: false,
      twoTitle: false,
      img: 'assets/img/menu/hotel.jpg',
      menu: [
        {
          menuValue: 'Hotel Grid',
          route: routes.hotelGrid,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'hotel-grid',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Hotel List',
          route: routes.hotelList,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'hotel-list',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Hotel Map',
          route: routes.hotelMap,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'hotel-map',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Hotel Details',
          route: routes.hotelDetails,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'hotel-details',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Hotel Booking',
          route: routes.bookingConfirmation,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'hotel-bookings',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Add Hotel',
          route: routes.addHotel,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'add-hotel',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
      ],
    },
    {
      tittle: 'Car',
      base: 'car',
      subTitle: 'Car Booking',
      showAsTab: false,
      separateRoute: false,
      twoTitle: false,
      img: 'assets/img/menu/car.jpg',
      menu: [
        {
          menuValue: 'Car Grid',
          route: routes.carGrid,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'car-grid',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Car List',
          route: routes.carList,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'car-list',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Car Map',
          route: routes.carMap,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'car-map',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Car Details',
          route: routes.carDetails,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'car-details',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Car Booking',
          route: routes.carBookingConfirmation,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'car-bookings',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Add Car',
          route: routes.addCar,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'add-car',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
      ],
    },
    {
      tittle: 'Cruise',
      base: 'cruise',
      subTitle: 'Cruise Booking',
      showAsTab: false,
      separateRoute: false,
      twoTitle: false,
      img: 'assets/img/menu/cruise.jpg',
      menu: [
        {
          menuValue: 'Cruise Grid',
          route: routes.cruiseGrid,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'cruise-grid',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Cruise List',
          route: routes.cruiseList,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'cruise-list',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Cruise Map',
          route: routes.cruiseMap,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'cruise-map',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Cruise Details',
          route: routes.cruiseDetails,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'cruise-details',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Cruise Booking',
          route: routes.cruiseBookingConfirmation,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'cruise-bookings',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Add Cruise',
          route: routes.addCruise,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'add-cruise',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
      ],
    },
    {
      tittle: 'Tour',
      base: 'tour',
      subTitle: 'Tour Booking',
      showAsTab: false,
      separateRoute: false,
      twoTitle: false,
      img: 'assets/img/menu/tour.jpg',
      menu: [
        {
          menuValue: 'Tour Grid',
          route: routes.tourGrid,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'tour-grid',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Tour List',
          route: routes.tourList,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'tour-list',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Tour Map',
          route: routes.tourMap,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'tour-map',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Tour Details',
          route: routes.tourDetails,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'tour-details',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Tour Booking',
          route: routes.tourBookingConfirmation,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'tour-bookings',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Add Tour',
          route: routes.addTour,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'add-tour',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
      ],
    },
    {
      tittle: 'Pages',
      base: 'pages',
      subTitle: 'Pages',
      subTitle2: ' ',
      showAsTab: false,
      separateRoute: false,
      twoTitle: true,
      img: 'assets/img/menu/flight.jpg',
      menu: [
        {
          menuValue: 'About',
          route: routes.aboutUs,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'about-us',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Gallery',
          route: routes.gallery,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'gallery',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Testimonials',
          route: routes.testimonial,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'testimonial',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Faq',
          route: routes.faq,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'faq',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Pricing Plan',
          route: routes.pricingPlan,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'pricing-plan',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Teams',
          route: routes.team,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'team',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Invoice',
          route: routes.invoices,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'invoices',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Blogs Grid',
          route: routes.blogGrid,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'blog-grid',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Blogs List',
          route: routes.blogList,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'blog-list',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Blogs Details',
          route: routes.blogDetails,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'blog-details',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Contact Us',
          route: routes.contactUs,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'contact-us',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Booking Confirmation',
          route: routes.bookingConfirmation,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'booking-confirmation',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Destination',
          route: routes.destination,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'destination',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Terms & Conditions',
          route: routes.termsConditions,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'Terms-Conditions',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Privacy Policy',
          route: routes.privacyPolicy,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'privacy-policy',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Login',
          route: routes.login,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'login',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Register',
          route: routes.register,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'register',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Forgot Password',
          route: routes.forgotPassword,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'forgot-password',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Change Password',
          route: routes.changePassword,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'change-password',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: '404 Error',
          route: routes.error404,
          hasSubRoute: false,
          showSubRoute: false,
          base: '404-error',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: '500 Error',
          route: routes.error500,
          hasSubRoute: false,
          showSubRoute: false,
          base: '500-error',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Under Maintenance',
          route: routes.underMaintenance,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'under-maintenance',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Coming Soon',
          route: routes.comingSoon,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'coming-soon',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'RTL',
          route: routes.indexRTL,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'index-rtl',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
      ],
    },
    {
      tittle: 'Dashboard',
      base: 'dashboard',
      subTitle: 'User Dashboard',
      subTitle2: 'Agent Dashboard',
      base1: 'user',
      base2: 'agent',
      showAsTab: false,
      separateRoute: false,
      twoTitle: true,
      img: 'assets/img/menu/flight.jpg',
      menu: [
        {
          menuValue: 'Dashboard',
          route: routes.userDashboard,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'dashboard',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'My Bookings',
          route: routes.customerFlightBooking,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'my-bookings',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Reviews',
          route: routes.review,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'reviews',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Message',
          route: routes.chat,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'message',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Wishlist',
          route: routes.wishlist,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'wishlist',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Wallet',
          route: routes.wallet,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'wallet',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Payments',
          route: routes.payment,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'payments',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Profile Settings',
          route: routes.profileSettings,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'profile-settings',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Notifications',
          route: routes.notification,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'notifications',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'My Profile',
          route: routes.myProfile,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'my-profile',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue: 'Settings',
          route: routes.securitySettings,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'settings',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Dashboard',
          route: routes.agentDashboard,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'admin-dashboard',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Listings',
          route: routes.agentListings,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'listings',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Bookings',
          route: routes.agentHotelBooking,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'bookings',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Enquiries',
          route: routes.agentEnquirers,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'enquiries',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Earnings',
          route: routes.agentEarnings,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'earnings',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Reviews',
          route: routes.agentReview,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'reviews',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
        {
          menuValue2: 'Settings',
          route: routes.agentSettings,
          hasSubRoute: false,
          showSubRoute: false,
          base: 'settings',
          page: '',
          last: '',
          subMenus: [],
          icon: ''
        },
      ],
    },


  ];
  public getSideBarData: BehaviorSubject<Array<SideBar>> = new BehaviorSubject<
    Array<SideBar>
  >(this.sideBar);


  getBookings(type: 'hotel' | 'flight' | 'car', page: number = 0, size: number = 10) {
    console.log('Fetching bookings of type:', type);
    switch (type) {
      case 'hotel':
        return this.getAllHotelBookings(page, size);
      case 'flight':
        return this.getAllFlightBookings(page, size);
      case 'car':

        return this.getAllCarBookings(page, size);
      default:
        throw new Error('Unknown booking type');
    }
  }




  normalizeBooking(type: 'flight' | 'hotel' | 'car', apiRes: any[]): any[] {
    if (!apiRes || !Array.isArray(apiRes)) return [];

    switch (type) {
      //  FLIGHT BOOKINGS
      case 'flight':
        return apiRes
          .map((booking: any) => {
            const tripDetails =
              booking?.tripDetailsResponse?.TripDetailsResponse?.TripDetailsResult?.TravelItinerary;
            if (!tripDetails) return null;
            return {
              id: tripDetails?.UniqueID,
              airLinePNR:
                tripDetails?.ItineraryInfo?.ReservationItems?.[0]?.ReservationItem
                  ?.MarketingAirlineCode,
              fromTo: `${tripDetails?.Origin} - ${tripDetails?.Destination}`,
              price: (booking?.paymentDetailsResponse?.amount || 0) / 100,
              currencyCode:
                tripDetails?.ItineraryInfo?.ItineraryPricing?.TotalFare
                  ?.CurrencyCode,
              date: new Date(
                tripDetails?.ItineraryInfo?.ReservationItems?.[0]?.ReservationItem
                  ?.DepartureDateTime
              ).toLocaleDateString(),
              status: booking?.bookingStatus || 'Unknown',
            };
          })
          .filter((x) => x !== null);

      case 'hotel':
        console.log(' Raw hotel API Response:', apiRes);
        return apiRes.map((booking: any) => {
          const details = booking?.roomBookDetails || booking;

          // Fallback handling
          const price =
            details?.totalFare ||
            booking?.totalFare ||
            details?.NetPrice ||
            booking?.paymentDetailsResponse?.amount / 100 ||
            '--';

          return {
            id:
              booking?.referenceNum ||
              booking?.clientRefNum ||
              booking?.supplierConfirmationNum ||
              '--',
            hotelName:
              details?.hotelName ||
              booking?.hotelName ||
              'Hotel not available',
            city: details?.city || booking?.city || 'N/A',
            checkIn: details?.checkIn || booking?.checkIn || 'N/A',
            checkOut: details?.checkOut || booking?.checkOut || 'N/A',
            price: price,
            status:
              booking?.bookingStatus ||
              details?.status ||
              booking?.status ||
              'Unknown',
          };
        });


      case 'car':
        console.log('🚗 Raw car API Response:', apiRes);

        return apiRes.map((booking: any) => {
          let parsedData: Record<string, any> = {}; //  Fix typing
          try {
            parsedData = booking?.trawexResponseData
              ? JSON.parse(booking.trawexResponseData)
              : {};
          } catch (e) {
            console.warn('⚠️ Failed to parse trawexResponseData for booking:', booking.id, e);
          }

          //  property access
          const rental = parsedData['rental'] ?? {};
          const car = rental?.carDetails ?? {};
          const pickup = rental?.pickup ?? {};
          const dropoff = rental?.dropoff ?? {};
          const vendor = rental?.vendor ?? {};
          const fee = rental?.fee ?? {};

          return {
            id: booking?.id ?? '--',
            carName: car?.carModel ?? 'N/A',
            pickupLocation: pickup?.locationName ?? 'N/A',
            dropoffLocation: dropoff?.locationName ?? 'N/A',
            driver: vendor?.name ?? 'N/A',
            price: fee?.rateTotalAmount ?? '--',
            currency: fee?.currencyCode ?? '',
            status: booking?.status ?? parsedData['status'] ?? 'Unknown'
          };
        });



    }
  }





}
